/**
 * Gift jar personalisation.
 * Message and Name are line item properties (shown in cart, checkout, and the order).
 * A print block is also written into the order note so the label can be printed from Notes.
 */
(() => {
  if (window.__llGiftJar) return;
  window.__llGiftJar = true;

  const CLEAN = /[^A-Za-z0-9 .,!?'’&\-]/g;
  const NOTE_START = 'GIFT LABEL — PRINT';
  const NOTE_END = 'END GIFT LABEL';

  const clean = (value, max) => String(value || '').replace(CLEAN, '').slice(0, max);

  /* Fixed sizes. Short words stay the same height as long ones.
     A line only shrinks when it would run past the blue underline. */
  const MESSAGE_SCALE = 0.164;
  const NAME_SCALE = 0.237;

  const fitLine = (el, text, box, scale) => {
    el.textContent = text;
    el.style.width = 'max-content';
    if (!box) return;
    let size = box * scale;
    el.style.fontSize = `${size}px`;
    if (el.scrollWidth > box && el.scrollWidth > 0) {
      size *= box / el.scrollWidth;
      el.style.fontSize = `${size}px`;
    }
  };

  const fitLabel = (messageEl, nameEl, message, person) => {
    const label = (messageEl || nameEl) && (messageEl || nameEl).closest('.ll-gift__label');
    const box = label ? label.clientWidth : 0;
    if (messageEl) fitLine(messageEl, message, box, MESSAGE_SCALE);
    if (nameEl) fitLine(nameEl, person, box, NAME_SCALE);
  };

  const cartUrl = (path) => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}${path}`;
  };

  const unpatchedFetch = (url, options) =>
    new Promise((resolve, reject) => {
      const iframe = document.createElement('iframe');
      iframe.setAttribute('hidden', '');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';
      let started = false;
      let settled = false;
      const finish = (cb, value) => {
        if (settled) return;
        settled = true;
        iframe.remove();
        cb(value);
      };
      const run = () => {
        if (started) return;
        started = true;
        const fetchFn = iframe.contentWindow && iframe.contentWindow.fetch;
        if (typeof fetchFn !== 'function') {
          finish(reject, new Error('Cart request failed'));
          return;
        }
        fetchFn
          .call(iframe.contentWindow, url, options)
          .then((res) =>
            res.text().then((text) => {
              const headers = new Headers();
              try {
                res.headers.forEach((value, key) => headers.set(key, value));
              } catch (err) {
                headers.set('Content-Type', 'application/json');
              }
              finish(
                resolve,
                new Response(text, {
                  status: res.status,
                  statusText: res.statusText,
                  headers,
                })
              );
            })
          )
          .catch((err) => finish(reject, err));
      };
      iframe.addEventListener('load', run, { once: true });
      document.documentElement.appendChild(iframe);
      try {
        if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') run();
      } catch (err) {
        /* iframe not ready yet */
      }
    });

  const request = (url, options) => unpatchedFetch(url, options).catch(() => fetch(url, options));

  const prop = (item, name) => {
    const props = item && item.properties;
    if (!props) return '';
    if (Array.isArray(props)) {
      const found = props.find((entry) => entry && (entry.name === name || entry[0] === name));
      if (!found) return '';
      return found.value || found[1] || '';
    }
    return props[name] || '';
  };

  const giftBlock = (items) => {
    const lines = [];
    (items || []).forEach((item) => {
      const message = String(prop(item, 'Message') || '').trim();
      const person = String(prop(item, 'Name') || '').trim();
      if (!message && !person) return;
      const qty = Number(item.quantity) > 1 ? ` x${item.quantity}` : '';
      const title = item.product_title || item.title || 'Gift jar';
      lines.push(`${title}${qty}`);
      lines.push(`Message: ${message.toUpperCase()}`);
      lines.push(`Name: ${person.toUpperCase()}`);
    });
    if (!lines.length) return '';
    return `${NOTE_START}\n${lines.join('\n')}\n${NOTE_END}`;
  };

  const mergeNote = (existing, block) => {
    const note = String(existing || '');
    const start = note.indexOf(NOTE_START);
    const end = start >= 0 ? note.indexOf(NOTE_END, start + NOTE_START.length) : -1;
    let rest = note;
    if (start >= 0 && end >= 0) {
      rest = `${note.slice(0, start)}${note.slice(end + NOTE_END.length)}`.replace(/\n{3,}/g, '\n\n').trim();
    }
    if (!block) return rest;
    return rest ? `${block}\n\n${rest}` : block;
  };

  let syncing = false;
  const syncGiftNote = async () => {
    if (syncing) return;
    syncing = true;
    try {
      const cartRes = await request(cartUrl('cart.js'), { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();
      const next = mergeNote(cart.note, giftBlock(cart.items));
      if (next === String(cart.note || '')) return;
      await request(cartUrl('cart/update.js'), {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: next }),
      });
      document.querySelectorAll('textarea[name="note"]').forEach((field) => {
        field.value = next;
      });
    } catch (err) {
      /* note sync is best-effort; line item properties still save the label */
    } finally {
      syncing = false;
    }
  };

  let noteTimer = 0;
  const queueNoteSync = () => {
    window.clearTimeout(noteTimer);
    noteTimer = window.setTimeout(syncGiftNote, 250);
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, queueNoteSync);
  }
  document.addEventListener('cart:updated', queueNoteSync);

  const init = (root) => {
    if (root.dataset.giftBound === 'true') return;
    root.dataset.giftBound = 'true';

    const form = root.querySelector('[data-gift-form]');
    if (!form) return;

    const maxMessage = Number(root.dataset.maxMessage) || 15;
    const maxName = Number(root.dataset.maxName) || 14;
    const available = root.dataset.available !== 'false';
    const messageInput = form.querySelector('[data-gift-message]');
    const typeInput = form.querySelector('[data-gift-type]');
    const customInput = form.querySelector('[data-gift-custom-input]');
    const nameInput = form.querySelector('[data-gift-name]');
    const confirmInput = form.querySelector('[data-gift-confirm]');
    const customPanel = form.querySelector('[data-gift-custom-panel]');
    const customButton = form.querySelector('[data-gift-custom]');
    const chips = [...form.querySelectorAll('[data-gift-preset]')];
    const labelMessage = root.querySelector('[data-gift-label-message]');
    const labelName = root.querySelector('[data-gift-label-name]');
    const messageCount = form.querySelector('[data-gift-message-count]');
    const nameCount = form.querySelector('[data-gift-name-count]');
    const atc = form.querySelector('[data-gift-atc]');
    const errorEl = form.querySelector('[data-gift-error]');

    let mode = 'preset';

    const selectedPreset = () => {
      const chip = chips.find((button) => button.classList.contains('is-selected'));
      return (chip && chip.dataset.label) || (chips[0] && chips[0].dataset.label) || '';
    };

    const state = () => {
      const custom = mode === 'custom';
      const customText = customInput ? customInput.value.trim() : '';
      const person = nameInput ? nameInput.value.trim() : '';
      const confirmed = confirmInput ? confirmInput.checked : false;
      const message = custom ? customText : selectedPreset();
      const ready = Boolean(person) && (!custom || (customText && confirmed));
      let label = 'Add their name';
      if (!available) label = 'Sold out';
      else if (ready) label = 'Add to basket';
      else if (custom && !customText) label = 'Type your message';
      else if (custom && !confirmed) label = 'Tick to confirm spelling';
      return { custom, customText, person, message, ready: available && ready, label };
    };

    const paint = () => {
      const current = state();
      root.classList.toggle('is-custom', current.custom);
      if (customButton) customButton.classList.toggle('is-selected', current.custom);
      if (customPanel) customPanel.hidden = !current.custom;
      if (messageInput) messageInput.value = current.message;
      if (typeInput) typeInput.value = current.custom ? 'Custom' : 'Preset';

      const previewMessage = current.custom ? current.customText || 'Your message' : current.message || 'Happy Birthday';
      const previewName = current.person || 'Their name';
      if (labelMessage || labelName) fitLabel(labelMessage, labelName, previewMessage, previewName);
      if (messageCount) messageCount.textContent = `${customInput ? customInput.value.length : 0} / ${maxMessage}`;
      if (nameCount) nameCount.textContent = `${nameInput ? nameInput.value.length : 0} / ${maxName}`;
      if (atc && available) {
        atc.disabled = !current.ready;
        atc.textContent = current.label;
      }
    };

    const selectPreset = (chip) => {
      mode = 'preset';
      chips.forEach((button) => button.classList.toggle('is-selected', button === chip));
      paint();
    };

    chips.forEach((chip) => {
      chip.addEventListener('click', () => selectPreset(chip));
    });

    if (customButton) {
      customButton.addEventListener('click', () => {
        mode = 'custom';
        chips.forEach((button) => button.classList.remove('is-selected'));
        paint();
        if (customInput) customInput.focus();
      });
    }

    const bindSanitised = (input, max) => {
      if (!input) return;
      input.addEventListener('input', () => {
        const next = clean(input.value, max);
        if (next !== input.value) input.value = next;
        paint();
      });
    };

    bindSanitised(customInput, maxMessage);
    bindSanitised(nameInput, maxName);
    if (confirmInput) confirmInput.addEventListener('change', paint);

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const current = state();
      if (!current.ready || !atc || atc.disabled) {
        paint();
        return;
      }

      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }
      atc.disabled = true;
      atc.textContent = 'Adding…';

      try {
        const drawer = document.querySelector('cart-drawer');
        const notification = document.querySelector('cart-notification');
        const cartUi = drawer || notification;
        const formData = new FormData(form);
        let sectionIds = 'cart-drawer,cart-icon-bubble';
        if (cartUi && typeof cartUi.getSectionsToRender === 'function') {
          sectionIds = cartUi
            .getSectionsToRender()
            .map((section) => section.id)
            .filter(Boolean)
            .join(',');
        }
        formData.append('sections', sectionIds);
        formData.append('sections_url', window.location.pathname);
        if (cartUi && typeof cartUi.setActiveElement === 'function') cartUi.setActiveElement(atc);

        const response = await request(cartUrl('cart/add.js'), {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Unable to add to cart.');
        }

        await syncGiftNote();

        if (drawer) {
          drawer.classList.remove('is-empty');
          drawer.querySelector('cart-drawer-items')?.classList.remove('is-empty');
        }
        if (cartUi && typeof cartUi.renderContents === 'function' && data.sections) {
          try {
            cartUi.renderContents(data);
          } catch (err) {
            if (drawer && typeof drawer.open === 'function') drawer.open(atc);
          }
        } else if (drawer && typeof drawer.open === 'function') {
          drawer.open(atc);
        }

        document.querySelectorAll('textarea[name="note"]').forEach((field) => {
          if (!field.value.includes(NOTE_START)) queueNoteSync();
        });

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-jar', cartData: data });
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'gift-jar', data } }));
      } catch (err) {
        if (errorEl) {
          errorEl.textContent = err.message || 'Unable to add to cart. Please try again.';
          errorEl.hidden = false;
        }
      } finally {
        paint();
      }
    });

    paint();
    if (typeof ResizeObserver === 'function') {
      const label = root.querySelector('.ll-gift__label');
      if (label) new ResizeObserver(paint).observe(label);
    }
  };

  const boot = () => document.querySelectorAll('[data-gift-jar]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target && event.target.querySelector && event.target.querySelector('[data-gift-jar]');
    if (root) init(root);
    if (event.target && event.target.matches && event.target.matches('[data-gift-jar]')) init(event.target);
  });
})();
